import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getAreaData } from '../data/areasData';
import { COMPANY_INFO, REVIEWS } from '../data/staticData';
import RoofTypesCarousel from '../components/RoofTypesCarousel';

export default function AreaPage() {
  const { area } = useParams();
  const data = getAreaData(area);

  // FAQ Accordion tilstand
  const [openFaq, setOpenFaq] = useState([0]);

  // Testimonial slider tilstand
  const [quoteIndex, setQuoteIndex] = useState(0);

  if (!data) {
    return <Navigate to="/nyt-tag" replace />;
  }

  const prevQuote = () => {
    setQuoteIndex((prev) => (prev === 0 ? REVIEWS.length - 1 : prev - 1));
  };

  const nextQuote = () => {
    setQuoteIndex((prev) => (prev === REVIEWS.length - 1 ? 0 : prev + 1));
  };

  const toggleFaq = (idx) => {
    setOpenFaq((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  // Parser simpel inline markdown for links ([tekst](url)) og fed skrift (**tekst**)
  const formatInlineText = (text) => {
    if (!text) return null;
    const parts = [];
    const regex = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      const token = match[0];
      if (token.startsWith('**') && token.endsWith('**')) {
        parts.push(
          <strong key={key++} className="font-bold text-stone-900">
            {token.slice(2, -2)}
          </strong>
        );
      } else if (token.startsWith('[') && token.includes('](')) {
        const linkText = token.slice(1, token.indexOf(']('));
        const linkUrl = token.slice(token.indexOf('](') + 2, -1);
        parts.push(
          <Link
            key={key++}
            to={linkUrl}
            className="text-[#BE0F12] font-semibold underline hover:text-stone-900 transition-colors"
          >
            {linkText}
          </Link>
        );
      }
      lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    return parts.length > 0 ? parts : text;
  };

  // Renderer en tekstblok med afsnit, punktopstilling og proceslister
  const renderTextContent = (content) => {
    if (!content) return null;
    const blocks = content.split('\n\n');

    return (
      <div className="space-y-4 text-stone-700 leading-relaxed text-sm sm:text-base">
        {blocks.map((block, idx) => {
          const trimmed = block.trim();
          if (!trimmed) return null;

          if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            const items = trimmed
              .split('\n')
              .map((line) => line.replace(/^[-*]\s*/, '').trim());
            return (
              <ul key={idx} className="list-disc pl-5 space-y-2 text-stone-700 my-2">
                {items.map((item, i) => (
                  <li key={i}>{formatInlineText(item)}</li>
                ))}
              </ul>
            );
          }

          if (/^\d+\.\s/.test(trimmed)) {
            const items = trimmed
              .split('\n')
              .map((line) => line.replace(/^\d+\.\s*/, '').trim());
            return (
              <ol key={idx} className="list-decimal pl-5 space-y-2 text-stone-700 my-2">
                {items.map((item, i) => (
                  <li key={i}>{formatInlineText(item)}</li>
                ))}
              </ol>
            );
          }

          return (
            <p key={idx} className="leading-relaxed">
              {formatInlineText(trimmed)}
            </p>
          );
        })}
      </div>
    );
  };

  // Eksisterende centrale FAQ-spørgsmål
  const FAQ_ITEMS = [
    {
      q: 'Hvad koster et nyt tag?',
      a: 'Prisen på et nyt tag afhænger af tagtype, størrelse, konstruktion og adgangsforhold. De fleste parcelhuse ligger typisk i et samlet spænd fra ca. 120.000 kr. og op, afhængigt af materialevalg og kompleksitet. Prøv vores online prisberegner for et konkret overslag til din bolig.'
    },
    {
      q: 'Hvilken type tag er den bedste til min bolig?',
      a: 'Det afhænger af husets arkitektur, hældning, budget og eventuelle lokalplaner. Tegltag og betontagsten er meget populære til traditionelle villaer og parcelhuse, mens ståltag og tagpap egner sig ideelt til huse med lav hældning eller tilbygninger.'
    },
    {
      q: 'Skal jeg have nyt undertag, når jeg får nyt tag?',
      a: 'I mange tilfælde ja. Undertaget er den del af tagkonstruktionen, der beskytter mod fugt og fygende sne. Er undertaget slidt eller mørnet, anbefaler vi altid at udskifte det samtidig for at sikre maksimal holdbarhed.'
    },
    {
      q: 'Kan man lægge nyt tag ovenpå det gamle?',
      a: 'I nogle tilfælde – f.eks. ved lette ståltagsløsninger som Decra – kan man montere det ovenpå et eksisterende tag, hvis den underliggende spærkonstruktion er sund og fri for fugtskader.'
    },
    {
      q: 'Kræver nyt tag byggetilladelse?',
      a: 'I de fleste tilfælde kræver en almindelig tagudskiftning ikke byggetilladelse, forudsat at tagets hældning og bærende konstruktion ikke ændres væsentligt. Skal du hæve taget eller inddrage tagetagen til beboelse, kræves der dog ansøgning hos kommunen.'
    },
    {
      q: 'Hvor lang tid tager det at lave et nyt tag?',
      a: 'De fleste tagudskiftninger tager 2–4 uger fra opstart til færdig aflevering afhængigt af vejret, tagets kompleksitet og størrelse. Du kan som regel blive boende i huset under hele forløbet.'
    }
  ];

  return (
    <div className="bg-white">
      {/* 1. Hero / Top-bar med lokal H1 og introduktion */}
      <section className="bg-stone-50 border-b border-stone-200 pt-10 sm:pt-14 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-stone-500 pb-2">
            <Link to="/" className="hover:text-[#BE0F12] transition-colors">
              Forside
            </Link>
            <span>/</span>
            <Link to="/nyt-tag" className="hover:text-[#BE0F12] transition-colors">
              Nyt tag
            </Link>
            <span>/</span>
            <span className="text-stone-900 font-semibold">{data.name}</span>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-xs font-bold text-[#BE0F12] uppercase tracking-wider">
            <i className="fa-solid fa-location-dot" aria-hidden="true"></i> Lokalområde: {data.name}
          </div>

          {/* H1 */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight uppercase max-w-4xl">
            {data.h1}
          </h1>

          {/* Eventuel H4/heroSubtitle (f.eks. på Horsens) */}
          {data.heroSubtitle && (
            <h2 className="text-xl sm:text-2xl font-bold text-stone-800 max-w-3xl">
              {data.heroSubtitle}
            </h2>
          )}

          {/* Lokal introduktion */}
          <div className="max-w-3xl space-y-4 text-stone-700 text-base sm:text-lg leading-relaxed pt-2">
            {data.introParagraphs.map((para, idx) => (
              <p key={idx}>{formatInlineText(para)}</p>
            ))}
          </div>

          {/* Nærområder badges */}
          {data.districts && data.districts.length > 0 && (
            <div className="pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-2.5">
                Vi dækker blandt andet:
              </p>
              <div className="flex flex-wrap gap-2">
                {data.districts.map((district) => (
                  <span
                    key={district}
                    className="px-3 py-1 bg-white text-stone-700 rounded-full text-xs font-medium border border-stone-200 shadow-2xs"
                  >
                    {district}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Hovedindhold: Sektioner før og efter Mulige tagtyper */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-16">
        
        {/* 2. Lokal tekst: Sektioner før karrusel */}
        {data.sectionsBeforeTypes && data.sectionsBeforeTypes.length > 0 && (
          <div className="space-y-12">
            {data.sectionsBeforeTypes.map((sec, sIdx) => (
              <section
                key={sIdx}
                className="bg-white border border-stone-200 p-6 sm:p-10 space-y-6 shadow-2xs"
                data-aos="fade-up"
              >
                <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
                  {sec.h2}
                </h2>
                {sec.intro && (
                  <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                    {formatInlineText(sec.intro)}
                  </p>
                )}
                {sec.content && renderTextContent(sec.content)}

                {sec.subsections && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                    {sec.subsections.map((sub, subIdx) => (
                      <div
                        key={subIdx}
                        className="bg-stone-50 border border-stone-200 p-5 sm:p-6 space-y-3"
                      >
                        <h3 className="text-lg font-bold text-stone-900 uppercase">
                          {sub.h3}
                        </h3>
                        {renderTextContent(sub.content)}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}

        {/* 3. Eksisterende sticky prisberegner-CTA */}
        <section
          className="bg-[#1c1c1c] text-white p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 border-l-4 border-[#BE0F12] shadow-sm"
          data-aos="fade-up"
        >
          <div className="space-y-2 text-center sm:text-left">
            <h3 className="text-xl sm:text-2xl font-extrabold uppercase">
              Få et uforpligtende tilbud – Prisberegner: Nyt tag
            </h3>
            <p className="text-sm text-stone-300 max-w-xl">
              Ud fra 4 simple spørgsmål beregner vi med det samme en uforpligtende pris til dig på nyt tag. Hurtigt og nemt!
            </p>
          </div>
          <Link
            to="/prisberegner"
            className="whitespace-nowrap px-6 py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white font-extrabold text-sm uppercase tracking-wider transition-colors shadow-md shrink-0"
          >
            Prisberegner: Nyt tag
          </Link>
        </section>

        {/* 4. Eksisterende sektion: “Mulige tagtyper” (horisontal karrusel) */}
        <RoofTypesCarousel />

        {/* 5. Lokal tekst: Sektioner efter karrusel */}
        {data.sectionsAfterTypes && data.sectionsAfterTypes.length > 0 && (
          <div className="space-y-12">
            {data.sectionsAfterTypes.map((sec, sIdx) => (
              <section
                key={sIdx}
                className="bg-white border border-stone-200 p-6 sm:p-10 space-y-6 shadow-2xs"
                data-aos="fade-up"
              >
                <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
                  {sec.h2}
                </h2>
                {sec.intro && (
                  <p className="text-stone-700 text-sm sm:text-base leading-relaxed">
                    {formatInlineText(sec.intro)}
                  </p>
                )}
                {sec.content && renderTextContent(sec.content)}

                {/* Eventuelle procestrin med nummerering */}
                {sec.processSteps && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-2">
                    {sec.processSteps.map((step, stepIdx) => (
                      <div
                        key={stepIdx}
                        className="bg-stone-50 border border-stone-200 p-5 space-y-2 flex flex-col justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-[#BE0F12] text-white font-bold flex items-center justify-center text-sm shrink-0">
                            {stepIdx + 1}
                          </span>
                          <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">
                            Trin {stepIdx + 1}
                          </span>
                        </div>
                        <p className="text-sm text-stone-800 leading-relaxed font-medium pt-1">
                          {formatInlineText(step)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {sec.subsections && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                    {sec.subsections.map((sub, subIdx) => (
                      <div
                        key={subIdx}
                        className="bg-stone-50 border border-stone-200 p-5 sm:p-6 space-y-3"
                      >
                        <h3 className="text-lg font-bold text-stone-900 uppercase">
                          {sub.h3}
                        </h3>
                        {renderTextContent(sub.content)}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}

        {/* 6. Eksisterende FAQ-sektion: “Find svar på ofte stillede spørgsmål” */}
        <section className="bg-stone-50 border border-stone-200 p-6 sm:p-10 space-y-6" data-aos="fade-up">
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
              Find svar på ofte stillede spørgsmål
            </h2>
            <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
              Her finder du svar på de mest almindelige spørgsmål i forbindelse med nyt tag, regler, materialer og processen.
            </p>
          </div>

          <div className="divide-y divide-stone-200 border-y border-stone-200 bg-white">
            {FAQ_ITEMS.map((item, idx) => {
              const isOpen = openFaq.includes(idx);
              return (
                <div key={idx} className="py-4 px-4 sm:px-6">
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between text-left font-bold text-base sm:text-lg text-stone-900 hover:text-[#BE0F12] transition-colors py-1 cursor-pointer"
                  >
                    <span>{item.q}</span>
                    <i
                      className={`fa-solid fa-chevron-down text-xs transition-transform ${
                        isOpen ? 'rotate-180 text-[#BE0F12]' : ''
                      }`}
                      aria-hidden="true"
                    ></i>
                  </button>
                  {isOpen && (
                    <div className="pt-3 pb-2 text-stone-700 text-sm leading-relaxed">
                      <p>{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* 7. Eksisterende CTA: “Kontakt mig, Lasse” */}
        <section
          className="bg-stone-100 border border-stone-200 p-6 sm:p-10"
          data-aos="fade-up"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Lasse Billeder */}
            <div className="lg:col-span-5 flex items-end justify-center gap-2 sm:gap-4">
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/har-du-sporgsmaal.png"
                alt="Har du spørgsmål, så tag fat i mig!"
                className="w-[42%] max-w-[140px] h-auto object-contain self-center"
              />
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/lasse-sidebar.png"
                alt="Lasse Vesterhouen - Dansk Tagbyg"
                className="w-[58%] max-w-[200px] h-auto object-contain"
              />
            </div>

            {/* Lasse Info & Knapper */}
            <div className="lg:col-span-7 space-y-4 text-center lg:text-left">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 uppercase">
                Kontakt mig, Lasse
              </h2>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Direkte rådgivning og uforpligtende besigtigelse. Jeg rådgiver dig ærligt om dit tags stand, materialer og muligheder.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 text-sm font-bold">
                <a
                  href={`tel:${COMPANY_INFO.phone}`}
                  className="w-full sm:w-auto px-6 py-3.5 bg-stone-900 hover:bg-black text-white text-center uppercase tracking-wider transition-colors shadow-xs"
                >
                  <i className="fa-solid fa-phone mr-2 text-xs"></i>
                  Ring: {COMPANY_INFO.phoneFormatted}
                </a>
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="w-full sm:w-auto px-6 py-3.5 bg-white border border-stone-300 hover:bg-stone-50 text-stone-800 text-center uppercase tracking-wider transition-colors shadow-xs"
                >
                  <i className="fa-solid fa-envelope mr-2 text-xs text-[#BE0F12]"></i>
                  Skriv: {COMPANY_INFO.email}
                </a>
                <Link
                  to="/kontakt"
                  className="w-full sm:w-auto px-6 py-3.5 bg-[#BE0F12] hover:bg-red-700 text-white text-center uppercase tracking-wider transition-colors shadow-xs"
                >
                  Kontakt os
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Eksisterende testimonial-sektion: “Vi motiveres af glade kunder og gode resultater” */}
        <section
          className="bg-stone-900 text-white p-6 sm:p-12 relative overflow-hidden"
          data-aos="fade-up"
        >
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-stone-800 pb-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-white">
                  Vi motiveres af glade kunder og gode resultater
                </h2>
                <div className="flex items-center gap-2 pt-2 text-amber-400 text-xs sm:text-sm">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star" aria-hidden="true"></i>
                    ))}
                  </div>
                  <span className="font-bold text-white">4,8 ud af 155 anmeldelser</span>
                  <span className="text-stone-500">•</span>
                  <span className="text-stone-300">Finalist til Årets Håndværker 5 år i træk</span>
                </div>
              </div>

              {/* Slider pile */}
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={prevQuote}
                  className="w-10 h-10 border border-stone-700 hover:border-white text-white flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Forrige anmeldelse"
                >
                  <i className="fa-solid fa-chevron-left text-sm" aria-hidden="true"></i>
                </button>
                <button
                  type="button"
                  onClick={nextQuote}
                  className="w-10 h-10 border border-stone-700 hover:border-white text-white flex items-center justify-center transition-colors cursor-pointer"
                  aria-label="Næste anmeldelse"
                >
                  <i className="fa-solid fa-chevron-right text-sm" aria-hidden="true"></i>
                </button>
              </div>
            </div>

            {/* Citat visning */}
            <div className="min-h-[140px] flex flex-col justify-between space-y-4">
              <p className="text-base sm:text-lg text-stone-200 italic leading-relaxed">
                "{REVIEWS[quoteIndex].quote}"
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-stone-800 text-xs sm:text-sm">
                <span className="font-bold text-white tracking-wide">
                  {REVIEWS[quoteIndex].author}, {REVIEWS[quoteIndex].location}
                </span>
                <div className="flex text-amber-400 text-xs">
                  {[...Array(REVIEWS[quoteIndex].rating || 5)].map((_, i) => (
                    <i key={i} className="fa-solid fa-star" aria-hidden="true"></i>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>
  );
}
